import dayjs from "dayjs";
import type { Point as HighchartsPoint, Tooltip as HighchartsTooltip, TooltipPositionerPointObject } from "highcharts";
import ReactDOMServer from "react-dom/server";

import { ChangeValue, ChartTooltip } from "@similarweb/ui-components";
import type { ColumnDefinition } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { FORMATTERS, PERCENTAGE_POINT } from "app/common/benchmark/formatters";

import { MetricFormat } from "app/common/benchmark/types";
import { Marker } from "../../common/Marker";

import { CenteredWrapper, NoDataTooltipValue, TooltipRightData, TooltipValue } from "./styles";

type TooltipDomain = { name?: string; color?: string };

type TooltipDataRow = {
    domains: TooltipDomain[];
    data: (number | undefined)[];
    change: (number | undefined)[];
};

const getDefaultTooltipColumns = (
    isWeekly: boolean,
    formatter: (val: number) => string,
    popDataFormatter: (val: number) => string,
    isInverted: boolean,
    tooltipMetricNameTitle: string,
): ColumnDefinition<TooltipDataRow>[] => [
    {
        key: "0",
        title: "Website",
        selector: "domains",
        align: "left",
        size: "172px",
        borderRight: false,
        render: ({ domains }: TooltipDataRow) => (
            <div>
                {domains?.map((item) => (
                    <CenteredWrapper key={item.name}>
                        <Marker backgroundColor={item.color ?? ""} />
                        <TooltipValue>{item.name}</TooltipValue>
                    </CenteredWrapper>
                ))}
            </div>
        ),
    },
    {
        key: "1",
        title: translate(tooltipMetricNameTitle),
        selector: "data",
        align: "right",
        size: "76px",
        render: ({ data }: TooltipDataRow) => (
            <TooltipRightData>
                {data?.map((item, i) => (
                    <TooltipValue key={i}>{item !== undefined ? formatter(item) : ""}</TooltipValue>
                ))}
            </TooltipRightData>
        ),
    },
    {
        key: "2",
        title: isWeekly ? "WoW" : "MoM",
        selector: "change",
        align: "right",
        size: "80px",
        render: ({ change }: TooltipDataRow) => (
            <TooltipRightData>
                {change?.map((num, i) => {
                    if (num === undefined) {
                        return <NoDataTooltipValue key={i}>-</NoDataTooltipValue>;
                    }
                    const convertToPlusValues = num < 0 ? -num : num;
                    const isDecrease = num < 0;
                    return (
                        <ChangeValue
                            key={`${i}-${num}`}
                            value={popDataFormatter(convertToPlusValues)}
                            invertColors={isInverted}
                            isDecrease={isDecrease}
                        />
                    );
                })}
            </TooltipRightData>
        ),
    },
];

const getDiffTooltipColumns = (
    formatter: (val: number) => string,
    isInverted: boolean,
): ColumnDefinition<TooltipDataRow>[] => [
    {
        key: "0",
        title: "Website",
        selector: "domains",
        align: "left",
        size: "172px",
        borderRight: false,
        render: ({ domains }: TooltipDataRow) => (
            <div>
                <CenteredWrapper>
                    <Marker backgroundColor={domains[0]?.color ?? ""} />
                    <TooltipValue>{domains[0]?.name}</TooltipValue>
                </CenteredWrapper>
            </div>
        ),
    },
    {
        key: "1",
        title: "Difference",
        selector: "change",
        align: "right",
        size: "80px",
        render: ({ change }: TooltipDataRow) => (
            <TooltipRightData>
                {change?.map((num, i) => {
                    if (num === undefined) {
                        return <NoDataTooltipValue key={i}>-</NoDataTooltipValue>;
                    }
                    const convertToPlusValues = num < 0 ? -num : num;
                    const isDecrease = num < 0;
                    return (
                        <ChangeValue
                            key={`${i}-${num}`}
                            value={formatter(convertToPlusValues)}
                            isDecrease={isInverted ? !isDecrease : isDecrease}
                            invertColors={isInverted}
                        />
                    );
                })}
            </TooltipRightData>
        ),
    },
];

export function tooltipPositioner(
    width: number,
    height: number,
    point: TooltipPositionerPointObject,
    isDifference: boolean,
    _this: HighchartsTooltip,
) {
    const xOffset = 80;
    const xOffsetOutOfScreen = 20;
    const offsetDifference = 20;

    const x = xOffset + point.plotX - width / 2;
    const screenWidth = window?.screen?.width;
    const tooltipPositionX = point?.plotX + width;
    const isOutOfScreen = tooltipPositionX > screenWidth;

    const positionX = isOutOfScreen ? x - (tooltipPositionX - screenWidth + xOffsetOutOfScreen) : x;

    if (isDifference) {
        const p = point as unknown as { negative?: boolean; h?: number };
        return {
            x: positionX - offsetDifference,
            y: p.negative ? point.plotY - (p.h ?? 0) - height : point.plotY - height - offsetDifference,
        };
    }
    const points = _this.chart?.hoverPoints ?? [];
    const firstPoint = points[0]?.plotY as number;
    const secondPoint = points[1]?.plotY as number;
    const minPoint = Math.min(firstPoint, secondPoint);

    return {
        x: positionX,
        y: minPoint - height,
    };
}

export const getTooltip = function (
    isWeekly: boolean,
    _this: HighchartsPoint,
    isDifferenceSelected: boolean,
    formatter: (val: number) => string,
    isInverted: boolean,
    tooltipMetricNameTitle: string,
    isRelative?: boolean,
) {
    const points = _this.points ?? [];
    const { key } = _this;
    const firstEntity = points[0];
    const firstEntityOptions = firstEntity?.series?.userOptions;

    const date = dayjs.utc(key).format(isWeekly ? "MMMM DD" : "MMMM YY");

    const secondEntity = points[1];
    const secondEntityOptions = secondEntity?.series?.userOptions;
    const percentFormatter = FORMATTERS[MetricFormat.percentSign];
    const PPFormatter = (val: number) => `${FORMATTERS[MetricFormat.percent](val)} ${PERCENTAGE_POINT}`;
    const popFormatter = isRelative ? PPFormatter : percentFormatter;
    const pointIndex = (_this as unknown as { point?: { index?: number } }).point?.index;

    type ExtSeriesOptions = { name?: string; color?: string; data?: Array<{ diff?: number }> };
    const firstOpts = firstEntityOptions as ExtSeriesOptions | undefined;
    const secondOpts = secondEntityOptions as ExtSeriesOptions | undefined;
    const tooltipData = isDifferenceSelected
        ? [
              {
                  domains: [{ name: firstOpts?.name, color: firstOpts?.color }],
                  data: [],
                  change: [firstEntity?.y],
              },
          ]
        : [
              {
                  domains: [
                      { name: firstOpts?.name, color: firstOpts?.color },
                      { name: secondOpts?.name, color: secondOpts?.color },
                  ],
                  data: [firstEntity?.y, secondEntity?.y],
                  change: [
                      // @ts-ignore pointIndex can be undefined — data[undefined] returns undefined as intended
                      firstOpts?.data?.[pointIndex]?.diff,
                      // @ts-ignore pointIndex can be undefined — data[undefined] returns undefined as intended
                      secondOpts?.data?.[pointIndex]?.diff,
                  ],
              },
          ];

    const tooltipColumns = isDifferenceSelected
        ? getDiffTooltipColumns(percentFormatter, isInverted)
        : getDefaultTooltipColumns(isWeekly, formatter, popFormatter, isInverted, tooltipMetricNameTitle);

    return ReactDOMServer.renderToStaticMarkup(
        <ChartTooltip<FixmeType>
            title={date}
            columnDefinition={tooltipColumns}
            data={tooltipData}
        />,
    );
};
